// Returns the current user's admin-panel access. Uses only requireSupabaseAuth
// so it never throws for non-admins — the caller uses the returned sections
// to filter UI / decide whether to redirect. Sections are DB-driven via
// `public.user_sections(auth.uid())` so the sidebar and the server API read
// the same source of truth.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  resolveAccessSections,
  type AdminSection,
} from "@/lib/admin-authz";

type Access = { roles: string[]; sections: AdminSection[] };

export const adminGetMyAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<Access> => {
    const supabase = context.supabase as {
      rpc: {
        (name: "has_role", args: { _user_id: string; _role: "admin" | "ops" | "customer" }):
          PromiseLike<{ data: boolean | null; error: { message: string } | null }>;
        (name: "user_sections", args: { _user_id: string }):
          PromiseLike<{ data: string[] | null; error: { message: string } | null }>;
      };
    };
    const [adminRes, opsRes, sectionsRes] = await Promise.all([
      supabase.rpc("has_role", { _user_id: context.userId, _role: "admin" }),
      supabase.rpc("has_role", { _user_id: context.userId, _role: "ops" }),
      supabase.rpc("user_sections", { _user_id: context.userId }),
    ]);
    const roles: string[] = [];
    if (adminRes.data) roles.push("admin");
    if (opsRes.data) roles.push("ops");

    // DB is authoritative; see resolveAccessSections for the fail-closed rule.
    const sections = resolveAccessSections({
      roles,
      sections: sectionsRes.data,
      rpcErrored: Boolean(sectionsRes.error),
    });
    return { roles, sections };
  });
